import { createAsyncThunk } from "@reduxjs/toolkit";

import { REQUEST_STATUSES, SORT_FIELD, SORT_ORIENTATION } from "@/constants";
import { ElementService } from "@/services";

import { deleteElement, setElements, setElementsRequestStatus } from "./elementsSlice";

// Types
interface FetchElementsParams {
  propertyId: string;
  experienceId: string;
  query?: Record<string, string | number | boolean>;
}

interface AddElementParams {
  propertyId: string;
  experienceId: string | number | null;
  values: Record<string, string | number | boolean | null>;
}

interface UpdateElementParams {
  propertyId: string;
  experienceId: string | number;
  elementId: number;
  values: Record<string, string | number | boolean | null>;
}

interface RemoveElementParams {
  propertyId: string;
  experienceId: string | number;
  elementId: number;
}

// Async thunks for Elements
export const fetchElements = createAsyncThunk(
  "elements/fetchElements",
  async ({ propertyId, experienceId, query = {} }: FetchElementsParams, { dispatch }) => {
    dispatch(setElementsRequestStatus({ propertyId, experienceId, status: REQUEST_STATUSES.LOADING }));

    try {
      const { resp, status, err } = await ElementService.getElements(propertyId, experienceId, query);

      dispatch(
        setElements({
          propertyId,
          experienceId,
          elements: resp?.data || [],
          status,
          err: err && typeof err === "object" && "message" in err ? String(err.message) : null,
        }),
      );

      return { resp, status, err };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      dispatch(
        setElements({
          propertyId,
          experienceId,
          elements: [],
          status: REQUEST_STATUSES.FAILED,
          err: errorMessage,
        }),
      );
      throw error;
    }
  },
);

export const addElement = createAsyncThunk(
  "elements/addElement",
  async ({ propertyId, experienceId, values }: AddElementParams, { dispatch }) => {
    if (!experienceId) {
      throw new Error("experienceId is required");
    }
    await ElementService.addElement(propertyId, String(experienceId), values);

    // Refetch elements after adding
    await dispatch(
      fetchElements({
        propertyId,
        experienceId: String(experienceId),
        query: {
          sortField: SORT_FIELD.ELEMENT_CREATED_AT,
          sortOrientation: SORT_ORIENTATION.ASC,
        },
      }),
    );

    return { success: true };
  },
);

export const updateElement = createAsyncThunk(
  "elements/updateElement",
  async ({ propertyId, experienceId, elementId, values }: UpdateElementParams, { dispatch }) => {
    await ElementService.updateElement(propertyId, String(experienceId), elementId, values);

    // Refetch elements after updating
    await dispatch(
      fetchElements({
        propertyId,
        experienceId: String(experienceId),
        query: {
          sortField: SORT_FIELD.ELEMENT_CREATED_AT,
          sortOrientation: SORT_ORIENTATION.ASC,
        },
      }),
    );

    return { success: true };
  },
);

export const removeElement = createAsyncThunk(
  "elements/removeElement",
  async ({ propertyId, experienceId, elementId }: RemoveElementParams, { dispatch }) => {
    await ElementService.deleteElement(propertyId, String(experienceId), elementId);

    dispatch(deleteElement({ propertyId, experienceId: String(experienceId), elementId }));

    return { success: true };
  },
);
